// Central configuration file for all game balance values
// This file contains all the constants used throughout the game for easy tuning

// Month name abbreviations for display
export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

// Helper to get month name from 1-indexed month number
export const getMonthName = (month: number): string => MONTH_NAMES[month - 1] || 'Jan';

export const GAME_CONSTANTS = {
  // Time and Simulation
  time: {
    tickRate: 1000, // milliseconds per game tick
    speedMultipliers: [1, 2, 3, 5], // available game speeds
    daysPerMonth: 30,
    monthsPerYear: 12,
    daysPerYear: 365,
    millisecondsPerDay: 24 * 60 * 60 * 1000,
    millisecondsPerMonth: 30 * 24 * 60 * 60 * 1000,
    startingDate: {
      year: 2000,
      month: 1,
      day: 1
    }
  },

  // Financial
  finance: {
    initialCash: 10_000_000, // $100M starting cash
    baseOperatingCosts: 2500, // per tick
    overheadCostMultiplier: 500, // multiplied by active products
    bankruptcyThreshold: -1_000_000, // $1M debt triggers bankruptcy
    bankruptcyWarnings: 3, // warnings before game over
    baseRevenuePerMarketShare: 1_000_000, // $1M per 1% market share
    operationalCostsRatio: 0.6 // 60% of revenue goes to operations
  },

  // Market
  market: {
    initialSize: 500_000_000, // 500M potential customers
    baseGrowthRate: 0.08, // 8% annual growth
    maxNegativeGrowth: -0.2, // -20% maximum market contraction
    minDailySales: 50, // minimum sales per product per day
    reputationSalesMultiplier: 2.5,
    marketShareSalesMultiplier: 2.0,
    marketPenetration: 0.2, // 20% market penetration
    annualPhonePurchaseRate: 0.3, // 30% of market buys phones annually
    newCompanyPenalty: 0.02, // 2% of theoretical demand for new companies
    updateInterval: 30, // Update market every 30 game days
    
    // Demand distribution factors (legacy - kept for backward compatibility)
    demandDistribution: {
      marketShareImpact: 0.4,     // 40% of demand based on market share
      productCompetitiveness: 0.3, // 30% based on product competitiveness
      segmentFit: 0.2,            // 20% based on segment fit
      randomFactor: 0.1           // 10% random distribution
    },

    // Attractiveness weights for normalized demand share calculation
    // These weights determine how product attributes affect demand share
    attractivenessWeights: {
      price: 0.25,        // How well price matches segment optimal range
      quality: 0.30,      // Tech score + market appeal combined
      brand: 0.20,        // Company reputation effect
      marketTiming: 0.10, // Era-based advantages (e.g., smartphone boom)
      productAge: 0.15    // Lifecycle position bonus/penalty
    },

    // Minimum demand share to prevent any product from being completely shut out
    minimumDemandShare: 0.05,

    // Brand score calculation (0.2 base + reputation * 0.8 scaling)
    brandScoreBase: 0.2,          // Minimum brand score for new companies
    brandScoreScale: 0.8,         // Maximum additional score from reputation

    // Market timing bonuses during smartphone boom (2007-2012)
    marketTimingBonus: {
      premium: 0.2,               // Premium segment with high tech score
      mainstream: 0.15            // Mainstream segment with good tech score
    },

    // Product age factor parameters
    ageFactor: {
      launchStart: 0.7,           // Starting factor at product launch
      launchRampUp: 0.3,          // Factor gained during launch phase (0-3 months)
      launchMonths: 3,            // Duration of launch phase in months
      growthPeak: 0.2,            // Peak bonus during growth phase
      growthMonths: 9,            // Duration of growth phase (months 3-12)
      matureMonths: 18,           // Start of decline after this many months
      declineBase: 1.0,           // Base factor at start of decline
      declineRate: 0.025,         // Factor reduction per month during decline
      declineFloor: 0.5           // Minimum factor during decline
    },

    // Random factor range for non-deterministic behavior
    randomFactor: {
      base: 0.97,                 // Minimum random multiplier
      range: 0.06                 // Range added to base (0.97-1.03)
    },

    // Minimum segment demand fallback (units per day)
    minimumSegmentDemand: 100,

    // Technology diffusion parameters (Bass model)
    innovationCoefficient: 0.03, // p - coefficient of innovation
    imitationCoefficient: 0.38, // q - coefficient of imitation
    disruptionProbability: 0.05, // 5% chance of disruption per update
    
    // Price multiplier bounds
    priceMultiplierMin: 0.1,
    priceMultiplierMax: 2.0,
    
    // Default values
    defaultTechScore: 50,
    defaultMarketAppeal: 50,
    
    // Competition
    baseCompetitionMultiplier: 0.1,
    competitionMultiplierRange: 0.9, // Range from 0.1 to 1.0
    competitionIntensityImpact: 0.5,
    
    // Innovation and satisfaction
    baseInnovation: 50,
    baseSatisfaction: 70,
    
    // Similarity thresholds
    priceSimilarityDivisor: 200,
    techSimilarityDivisor: 100,
    
    // Adoption curve
    defaultAdoptionRate: 0.5,
    adoptionRateMin: 0.15,
    adoptionRateRange: 0.35, // 0.15 to 0.5
    adoptionMidpointMonths: 6,
    
    // Active products threshold
    minActiveProducts: 1,
    
    // Multiplier bounds
    qualityMultiplierMin: 0.3,
    qualityMultiplierMax: 1.5,
    competitionMultiplierMin: 0.05,
    competitionMultiplierMax: 1.5,
    marketMultiplierMin: 0.5,
    marketMultiplierMax: 2.0,
    timeMultiplierMin: 0.3,
    timeMultiplierMax: 1.5,
    
    // Market share calculation
    marketSharePower: 0.5, // Power factor for market share impact
    marketShareDivisor: 100,
    
    // Default competition intensity
    defaultCompetitionIntensity: 0.2,
    
    // Economic impact factors
    economicImpactMultiplier: 0.1,
    
    // Customer satisfaction bonuses
    qualityBonusThreshold: 70,
    qualityBonus: 10,
    budgetPriceThreshold: 500,
    budgetBonus: 10,
    premiumPenalty: -5,
    
    // Innovation calculation
    innovationTechBonus: 0.4,
    
    // Adoption curve base
    adoptionCurveBase: 0.5
  },

  // Reputation
  reputation: {
    gainPerProduct: 0.02, // Keep for backward compatibility
    gainPerSuccessfulProduct: 0.5, // reputation gain for products with rating >= 4.0
    lossPerPoorProduct: 0.3, // reputation loss for products with rating < 3.0
    decayRate: 0.995, // reputation decay per tick
    decayThreshold: 70, // only decay when reputation exceeds this
    gainPerEmployeeHire: 0.1, // reputation gain per employee hired

    // Sales-based reputation growth
    salesReputationGainRate: 0.00001, // Reputation gain per unit sold (balanced for high volume)
    marketShareReputationMultiplier: 0.05, // Reputation bonus per % market share
    reputationGrowthCap: 1.0, // Maximum daily reputation gain from sales
    salesVolumeThresholds: {
      low: 100,      // Units per day for low tier
      medium: 1000,  // Units per day for medium tier
      high: 10000    // Units per day for high tier
    },

    // Market share tier bonuses for reputation growth
    marketShareTiers: {
      minimal: { threshold: 0.05, multiplier: 1.0 },    // < 5% market share
      small: { threshold: 0.10, multiplier: 1.25 },     // 5-10% market share
      moderate: { threshold: 0.25, multiplier: 1.5 },   // 10-25% market share
      large: { threshold: 0.50, multiplier: 2.0 },      // 25-50% market share
      dominant: { threshold: 1.0, multiplier: 3.0 }     // > 50% market share
    }
  },

  // Production
  production: {
    baseLaborCostPerUnit: 100,
    inefficiencyPenalty: 0.2, // 20% cost increase for inefficiency
    baseOverheadPerUnit: 50,
    manufacturingOverhead: 0.15, // 15% of base cost (reduced from 30%)
    assemblyCost: 0.10, // 10% of base cost (reduced from 20%)
    qualityControlCost: 0.05, // 5% of base cost (reduced from 10%)
    productionLineLockMonths: 6 // Months before production line can be reassigned
  },

  // Development
  development: {
    initialConfidence: 50, // Initial confidence score for new development phases
    initialQuality: 50, // Initial quality score for new development phases
    investmentQualityDiminishingSteps: 3, // Hard limit for diminishing returns on investment quality gains
    phaseDurations: {
      design: 7,
      engineering: 10,
      testing: 10,
      manufacturing: 10
    }
  },

  // Product Metrics
  productMetrics: {
    basePriceReference: 999, // reference price for calculations
    techScoreWeight: 0.7, // 70% weight for tech in calculations
    priceRatioWeight: 0.3, // 30% weight for price
    userSatisfactionTechWeight: 0.6,
    userSatisfactionValueWeight: 0.4,
    satisfactionMultiplier: 0.9,
    qualityBaseMultiplier: 0.8,
    complexityFactorMin: 0.9,
    complexityFactorMax: 1.1
  },

  // Quality Control
  quality: {
    baseDefectRate: 5, // percentage
    techScoreDefectReduction: 0.04, // reduction per tech score point
    defectRateVariation: 2, // +/- variation
    minDefectRate: 0.1, // minimum possible defect rate
    maxDefectRate: 10 // maximum possible defect rate
  },

  // Reviews and Ratings
  reviews: {
    numProfessionalReviews: 2, // Number of professional reviews per product
    numCustomerReviews: 3, // Number of customer reviews per product
    professionalWeightBase: 0.7, // Base weight for professional reviews
    customerWeightBase: 0.3, // Base weight for customer reviews
    minRating: 1, // Minimum review rating
    maxRating: 5 // Maximum review rating
  },

  // Business Calculations
  business: {
    techScoreWeight: 0.7, // 70% importance of tech
    priceAppealDivisor: 1000,
    priceAppealWeight: 30
  },

  // Research System
  research: {
    baseRPRate: 0.001, // RP generation rate per $1000
    maxConcurrentResearch: 2, // Maximum concurrent research projects
    costPerAllocationPoint: 100,
    bonusIncreasePerLevel: 0.1, // 10% bonus per level
    
    // Technology Branch
    technology: {
      cpuTier2: { cost: 500, duration: 14 },
      gpuTier2: { cost: 450, duration: 12 },
      batteryTech: { cost: 400, duration: 10 },
      cpuTier3: { cost: 1500, duration: 30 },
      advancedCamera: { cost: 1200, duration: 20 },
      fivePlusTech: { cost: 1000, duration: 25 },
      neuralProcessing: { cost: 3000, duration: 45 },
      holographicDisplay: { cost: 5000, duration: 60 }
    },
    
    // Design Branch
    design: {
      premiumMaterials: { cost: 300, duration: 7 },
      formFactors: { cost: 800, duration: 20 },
      extremeSizes: { cost: 250, duration: 5 },
      exoticMaterials: { cost: 1000, duration: 15 },
      modularDesign: { cost: 1500, duration: 30 },
      ergonomics: { cost: 600, duration: 10 },
      transparentTech: { cost: 4000, duration: 50 }
    },
    
    // Business Branch
    business: {
      researchEfficiency: { cost: 200, duration: 7 },
      productionOptimization: { cost: 400, duration: 14 },
      operationalEfficiency: { cost: 350, duration: 10 },
      advancedRnD: { cost: 800, duration: 20 },
      qualityManagement: { cost: 600, duration: 15 },
      supplyChain: { cost: 1000, duration: 25 },
      innovationLab: { cost: 2500, duration: 40 },
      fullAutomation: { cost: 3500, duration: 50 }
    },
    
    // Initial R&D Projects
    initialProjects: {
      advancedProcessor: { cost: 50000, duration: 30 },
      quantumMemory: { cost: 75000, duration: 45 },
      aiOptimization: { cost: 60000, duration: 35 },
      neuralInterface: { cost: 100000, duration: 60 },
      automatedAssembly: { cost: 40000, duration: 25 },
      smartFactory: { cost: 80000, duration: 50 },
      workflowOptimization: { cost: 30000, duration: 20 },
      qualityManagement: { cost: 50000, duration: 30 }
    }
  },

  // Employee Management
  employee: {
    hiringDays: 30, // Days to hire a new employee
    reputationLossPerFiring: 1.0, // Reputation loss per employee fired
    severanceMultiplier: 0.5, // Severance = monthly salary * multiplier
    maxReputationLossPerAction: 10, // Max reputation loss from firing
    minPerformance: 50, // Minimum employee performance
    maxPerformance: 100, // Maximum employee performance
    initialPerformance: 75, // Starting performance for new hires
    initialHappiness: 80, // Starting happiness for new hires
    
    // Role identifiers (used internally)
    roleIds: {
      ENGINEER: 'engineer',
      DESIGNER: 'designer',
      MARKETER: 'marketer',
      MANAGER: 'manager',
      RESEARCHER: 'researcher',
      TESTER: 'tester',
      ASSEMBLY_WORKER: 'assemblyWorker'
    },
    
    // Daily fluctuations
    dailyPerformanceVariation: 2, // ±1 range
    dailyHappinessVariation: 2, // ±1 range
    dailyExperienceGain: 0.1, // Experience gain per day
    
    // Productivity factors
    noEmployeePenalty: 0.5, // Penalty when no employees
    performanceWeight: 0.5, // Weight of performance in productivity
    happinessWeight: 0.6, // Weight of happiness in productivity
    skillWeight: 0.4, // Weight of skills in productivity
    maxProductivityBonus: 1.5, // Maximum productivity multiplier
    minProductivityPenalty: 0.2, // Minimum productivity multiplier
    productivityClampMin: 0.1, // Minimum allowed productivity
    productivityClampMax: 2.0, // Maximum allowed productivity
    
    // Team composition
    idealTeamRatio: {
      engineer: 4,
      designer: 2,
      marketer: 2,
      manager: 1
    },
    deviationPenalty: 0.5, // Penalty per point of deviation from ideal ratio
    
    // Annual salaries (converted to daily in service)
    annualSalaries: {
      engineer: 120000,
      designer: 100000,
      marketer: 90000,
      manager: 150000,
      researcher: 110000,
      tester: 80000,
      assemblyWorker: 50000
    },

    // Skill values for each role
    skills: {
      engineer: { technical: 80, creativity: 40, management: 20 },
      designer: { technical: 30, creativity: 90, management: 20 },
      marketer: { technical: 20, creativity: 50, management: 30 },
      manager: { technical: 40, creativity: 30, management: 90 },
      researcher: { technical: 90, creativity: 60, management: 10 },
      tester: { technical: 60, creativity: 20, management: 15 },
      assemblyWorker: { technical: 30, creativity: 10, management: 5 }
    },

    // Employee requirement scaling factors
    employeeScaling: {
      // Tech complexity multiplier - higher tech products need more employees
      techComplexityBase: 1.0, // Base multiplier
      techComplexityFactor: 0.00, // Additional employees per tech score point
      
      // Time progression multiplier - later years need more employees
      timeProgressionBase: 1.0, // Base multiplier for year 2000
      timeProgressionFactor: 0.02, // 2% increase per year after 2000
      
      // Maximum multipliers to prevent excessive requirements
      maxTechMultiplier: 2.0, // Maximum 2x employees for high tech
      maxTimeMultiplier: 1.5, // Maximum 1.5x employees for later years
    },

    // Development stage employee type requirements
    stageRequirements: {
      design: 'designer',
      engineering: 'engineer',
      testing: 'tester',
      manufacturing: 'assemblyWorker',
      production: 'assemblyWorker'
    },
    
    // Required roles per development phase (for future multi-role support)
    requiredRoles: {
      design: ['designer'],
      engineering: ['engineer'],
      testing: ['tester'],
      manufacturing: ['assemblyWorker'],
      production: ['assemblyWorker']
    },

    // Minimum employees per phase per role
    minEmployeesPerPhase: {
      design: { designer: 3 },
      engineering: { engineer: 3 },
      testing: { tester: 3 },
      manufacturing: { assemblyWorker: 3 },
      production: { assemblyWorker: 3 }
    }
  },

  // Database
  database: {
    version: 9
  },

  // UI/UX Constants
  ui: {
    eventLogLimit: 100, // maximum events to display
    defaultNotificationDuration: 5000 // milliseconds
  },

  // Dashboard
  dashboard: {
    tabs: ['Headquarters', 'Products', 'Operations', 'Research', 'Marketing', 'Finance', 'Calendar', 'Company'] as const,
    defaultTab: 'Headquarters' as const,
  },

  // Navigation
  navigation: {
    modes: ['spatial', 'classic'] as const,
    defaultMode: 'spatial' as const,
    transition: {
      defaultType: 'fade' as const,
      defaultDuration: 300,
      types: ['fade', 'slide', 'zoom', 'none'] as const,
    },
  },

  // Market Segments
  marketSegments: {
    // Segment names (for string constants)
    names: {
      BUDGET: 'Budget',
      BUDGET_CONSCIOUS: 'Budget Conscious',
      MAINSTREAM: 'Mainstream',
      MAINSTREAM_USERS: 'Mainstream Users',
      PREMIUM: 'Premium',
      PREMIUM_ENTHUSIASTS: 'Premium Enthusiasts',
      ENTERPRISE: 'Enterprise',
      ENTERPRISE_USERS: 'Enterprise Users',
      INNOVATIVE: 'Innovative'
    },
    
    // Segment base sizes (2000 baseline)
    sizes: {
      budget: 150_000_000,
      mainstream: 250_000_000,
      premium: 80_000_000,
      innovative: 50_000_000,
      enterprise: 50_000_000
    },
    
    // Year-based market growth rates
    growthRatesByEra: {
      featurePhone: { years: [2000, 2006], rate: 0.03 },    // 3% annual growth
      earlySmartphone: { years: [2007, 2009], rate: 0.15 }, // 15% boom period
      mainstream: { years: [2010, 2014], rate: 0.10 },      // 10% sustained growth
      mature: { years: [2015, 2030], rate: 0.05 }           // 5% mature market
    },
    
    // Year-based market evolution curves
    marketEvolution: {
      // Feature phone era (2000-2007)
      featurePhoneEra: {
        startYear: 2000,
        endYear: 2007,
        segmentMultipliers: {
          budget: 1.2,      // Budget phones dominate
          mainstream: 0.8,  // Mainstream smaller
          premium: 0.6,     // Premium niche
          enterprise: 1.0   // Stable enterprise
        }
      },
      // Smartphone introduction (2007-2010)
      smartphoneIntroduction: {
        startYear: 2007,
        endYear: 2010,
        segmentMultipliers: {
          budget: 1.0,      // Budget stabilizes
          mainstream: 1.0,  // Mainstream grows
          premium: 1.2,     // Premium early adopters
          enterprise: 1.1   // Enterprise adoption
        }
      },
      // Mainstream adoption (2010-2015)
      mainstreamAdoption: {
        startYear: 2010,
        endYear: 2015,
        segmentMultipliers: {
          budget: 0.8,      // Budget shrinks
          mainstream: 1.3,  // Mainstream dominates
          premium: 1.3,     // Premium grows
          enterprise: 1.2   // Enterprise grows
        }
      },
      // Market maturity (2015+)
      marketMaturity: {
        startYear: 2015,
        endYear: 2030,
        segmentMultipliers: {
          budget: 0.7,      // Budget becomes niche
          mainstream: 1.2,  // Mainstream stable
          premium: 1.5,     // Premium flourishes
          enterprise: 1.3   // Enterprise stable
        }
      }
    },
    
    // Price elasticity for each segment
    priceElasticity: {
      budget: 1.8, // Very price sensitive
      mainstream: 1.2, // Moderately price sensitive
      premium: 0.8, // Less price sensitive
      innovative: 0.6, // Least price sensitive
      enterprise: 0.6 // Least price sensitive
    },
    
    // Optimal price ranges
    priceRanges: {
      budget: { min: 200, max: 300 },
      mainstream: { min: 400, max: 700 },
      premium: { min: 800, max: 1200 },
      innovative: { min: 600, max: 1000 },
      enterprise: { min: 600, max: 1000 }
    },
    
    // Tech sensitivity (how much they value tech score)
    techSensitivity: {
      budget: 0.6,
      mainstream: 0.8,
      premium: 1.5,
      innovative: 1.0,
      enterprise: 1.0
    },
    
    // Brand loyalty
    brandLoyalty: {
      budget: 0.4,
      mainstream: 0.6,
      premium: 0.8,
      innovative: 0.9,
      enterprise: 0.9
    },
    
    // Price thresholds for segment classification
    priceThresholds: {
      budget: 300,
      mainstream: 700,
      premium: 1200
    },
    
    // Technology adoption by year
    technologyAdoptionByYear: {
      2000: { smartphone: 0.01, touchscreen: 0.01, apps: 0.01 },
      2005: { smartphone: 0.05, touchscreen: 0.02, apps: 0.03 },
      2007: { smartphone: 0.10, touchscreen: 0.15, apps: 0.08 }, // iPhone launch
      2008: { smartphone: 0.15, touchscreen: 0.25, apps: 0.15 },
      2009: { smartphone: 0.25, touchscreen: 0.40, apps: 0.30 },
      2010: { smartphone: 0.40, touchscreen: 0.60, apps: 0.50 }, // Android growth
      2011: { smartphone: 0.55, touchscreen: 0.75, apps: 0.65 },
      2012: { smartphone: 0.70, touchscreen: 0.85, apps: 0.80 },
      2013: { smartphone: 0.80, touchscreen: 0.90, apps: 0.85 },
      2014: { smartphone: 0.85, touchscreen: 0.93, apps: 0.90 },
      2015: { smartphone: 0.90, touchscreen: 0.95, apps: 0.93 }, // Maturity
      2020: { smartphone: 0.95, touchscreen: 0.98, apps: 0.97 }
    },
    
    // Product competitiveness factors
    productCompetitiveness: {
      techScoreWeight: 0.3,
      priceValueWeight: 0.3,
      marketTimingWeight: 0.2,
      brandWeight: 0.2
    },
    
    // Segment migration rates per year
    segmentMigration: {
      economicBoom: {
        budgetToMainstream: 0.05,
        mainstreamToPremium: 0.03
      },
      economicNormal: {
        budgetToMainstream: 0.02,
        mainstreamToPremium: 0.01
      },
      economicRecession: {
        premiumToMainstream: 0.04,
        mainstreamToBudget: 0.03
      }
    }
  },

  // Sales and Lifecycle
  sales: {
    // Seasonal factors by quarter (Q1-Q4)
    seasonalFactors: [0.8, 1.0, 0.9, 1.3],
    
    // Lifecycle stage names
    lifecycleStages: {
      LAUNCH: 'Launch',
      GROWTH: 'Growth',
      MATURITY: 'Maturity',
      DECLINE: 'Decline'
    },
    
    // Active lifecycle stages (for filtering)
    activeLifecycleStages: ['launch', 'growth', 'maturity'],
    
    // Product status (for sales eligibility)
    productionStatus: 'released',
    
    // Lifecycle stage durations (in months)
    lifecycleDurations: {
      launch: 3,
      growth: 9, // 3-12 months
      maturity: 12, // 12-24 months
      decline: Infinity,
      // For stage progress calculation
      declineProgressDivisor: 12
    },
    
    // Lifecycle multipliers
    lifecycleMultipliers: {
      launch: { min: 0.3, max: 0.8 },
      growth: { min: 0.8, max: 1.5 },
      maturity: { min: 1.0, max: 1.2 },
      decline: { min: 0.3, max: 0.9 }
    },
    
    // Adoption curve parameters
    adoptionRate: 0.5, // Rate of adoption curve
    adoptionMidpoint: 6, // Months until 50% adoption
    
    // Quality thresholds
    qualityWeights: {
      tech: 0.3,
      appeal: 0.3,
      userScore: 0.4
    },
    
    // Component score weights
    componentScoreWeights: {
      components: 0.25,
      innovation: 0.25,
      brand: 0.25,
      satisfaction: 0.25
    },
    
    // Seasonal factors by month (Jan-Dec)
    monthlySeasonalFactors: [0.7, 0.7, 0.8, 0.8, 0.9, 0.9, 0.95, 1.0, 1.1, 1.2, 1.3, 1.4],
    
    // Event impact
    defaultEventMultiplier: 1.0
  },

  // Economic Cycle
  economic: {
    // Phase names
    phases: {
      RECESSION: 'recession',
      RECOVERY: 'recovery',
      EXPANSION: 'expansion',
      PEAK: 'peak',
      CONTRACTION: 'contraction',
      TROUGH: 'trough'
    },
    
    // Phase durations (in months)
    phaseDurations: {
      expansion: { min: 18, max: 36 },
      peak: { min: 6, max: 12 },
      contraction: { min: 12, max: 24 },
      trough: { min: 6, max: 12 }
    },
    
    // Phase multipliers
    phaseMultipliers: {
      expansion: 1.2,
      peak: 1.4,
      contraction: 0.8,
      trough: 0.5,
      recovery: 0.95
    },
    
    // Economic phase modifiers
    phaseModifiers: {
      recession: { 
        demandModifier: 0.6, 
        priceSensitivityBonus: 0.3, 
        premiumImpact: -0.5 
      },
      recovery: { 
        demandModifier: 0.85, 
        priceSensitivityBonus: 0.1, 
        premiumImpact: -0.2 
      },
      expansion: { 
        demandModifier: 1.2, 
        priceSensitivityBonus: -0.1, 
        premiumImpact: 0.2 
      },
      peak: { 
        demandModifier: 1.4, 
        priceSensitivityBonus: -0.2, 
        premiumImpact: 0.4 
      },
      contraction: { 
        demandModifier: 0.95, 
        priceSensitivityBonus: 0.2, 
        premiumImpact: -0.3 
      }
    },
    
    // Economic indicators
    shockProbability: 0.02, // 2% per month
    recoveryCap: 0.95, // Maximum recovery multiplier
    
    // Indicator weights for market multiplier
    indicatorWeights: {
      gdp: 2,
      confidence: 1,
      unemployment: 1,
      inflation: 1
    }
  },

  // Quality Control
  qualityControl: {
    // Test scores for different categories
    testScores: {
      functionality: 92,
      reliability: 95,
      usability: 85,
      performance: 94,
      defective: 0
    },
    
    // Pass thresholds
    passThresholds: {
      minimum: 80,
      target: 90,
      excellent: 95
    }
  },

  // Mock Data Values (for development/testing)
  mockData: {
    revenue: 2_500_000,
    profitMargin: 28.5,
    sales: 12_500,
    growthRate: 15.2,
    departmentAllocation: {
      manufacturing: 45,
      rnd: 18,
      marketing: 13,
      operations: 24
    }
  },

  // Buildings System
  buildings: {
    // Starting building (free)
    startingBuilding: 'smallHeadquarters',
    
    // Building types
    types: {
      smallHeadquarters: {
        id: 'smallHeadquarters',
        name: 'Small Headquarters',
        description: 'Company headquarters for your startup',
        cost: 0, // Starting building is free
        capacity: {
          officeWorkers: 20,
          factoryWorkers: 20,
          technicalWorkers: 20,
        },
        maintenanceCost: 5_000, // per month
        category: 'headquarters',
        unlockDate: { year: 9999, month: 1 }, // Set to future year so it never appears in purchase list
        // reputationRequirement: 0, // COMMENTED OUT - may be added back later
        canSell: false, // Cannot sell the starting building
        constructionTime: 0, // Instant, as it's the starting building
        upgradesTo: 'mediumHeadquarters',
        upgradeCost: 3_500_000, // Cheaper than buying new
        upgradeTime: 30, // 30 days
        productionLines: 1,
        productionCapacity: 50
      },
      smallOffice: {
        id: 'smallOffice',
        name: 'Small Office',
        description: 'Basic office space for business operations',
        cost: 1_000_000,
        capacity: {
          officeWorkers: 30,
          factoryWorkers: 0,
          technicalWorkers: 0,
        },
        maintenanceCost: 8_000, // per month
        category: 'office',
        unlockDate: { year: 2001, month: 3 },
        // reputationRequirement: 5, // COMMENTED OUT - may be added back later
        canSell: true,
        sellValue: 500_000,
        constructionTime: 20, // 20 days
        upgradesTo: 'mediumOffice',
        upgradeCost: 1_500_000, // Cheaper than buying new
        upgradeTime: 20 // 20 days
      },
      mediumOffice: {
        id: 'mediumOffice',
        name: 'Medium Office',
        description: 'Expanded office space for a growing company',
        cost: 2_000_000,
        capacity: {
          officeWorkers: 50,
          factoryWorkers: 0,
          technicalWorkers: 0,
        },
        maintenanceCost: 20_000,
        category: 'office',
        unlockDate: { year: 2002, month: 6 },
        // reputationRequirement: 10, // COMMENTED OUT - may be added back later
        canSell: true,
        sellValue: 1_000_000, // 50% of purchase price
        constructionTime: 30, // 30 days
        upgradesFrom: 'smallOffice',
        upgradesTo: 'largeOffice',
        upgradeCost: 3_500_000, // Cheaper than buying new
        upgradeTime: 40 // 40 days
      },
      largeOffice: {
        id: 'largeOffice',
        name: 'Large Office',
        description: 'Professional office complex with modern amenities',
        cost: 5_000_000,
        capacity: {
          officeWorkers: 150,
          factoryWorkers: 0,
          technicalWorkers: 0,
        },
        maintenanceCost: 50_000,
        category: 'office',
        unlockDate: { year: 2005, month: 1 },
        // reputationRequirement: 25, // COMMENTED OUT - may be added back later
        canSell: true,
        sellValue: 2_500_000,
        constructionTime: 60, // 60 days
        upgradesFrom: 'mediumOffice'
      },
      mediumHeadquarters: {
        id: 'mediumHeadquarters',
        name: 'Medium Headquarters',
        description: 'Expanded headquarters with dedicated facilities',
        cost: 7_000_000,
        capacity: {
          officeWorkers: 50,
          factoryWorkers: 50,
          technicalWorkers: 50,
        },
        maintenanceCost: 40_000,
        category: 'headquarters',
        unlockDate: { year: 2004, month: 6 },
        // reputationRequirement: 20, // COMMENTED OUT - may be added back later
        canSell: true,
        sellValue: 3_500_000,
        constructionTime: 60, // 60 days
        upgradesFrom: 'smallHeadquarters',
        upgradesTo: 'corporateHQ',
        upgradeCost: 12_000_000, // Cheaper than buying new
        upgradeTime: 90, // 90 days
        productionLines: 2,
        productionCapacity: 100
      },
      corporateHQ: {
        id: 'corporateHQ',
        name: 'Corporate Headquarters',
        description: 'State-of-the-art corporate campus with facilities for all employee types',
        cost: 20_000_000,
        capacity: {
          officeWorkers: 100,
          factoryWorkers: 100,
          technicalWorkers: 100,
        },
        maintenanceCost: 150_000,
        category: 'headquarters',
        unlockDate: { year: 2010, month: 1 },
        // reputationRequirement: 50, // COMMENTED OUT - may be added back later
        canSell: true,
        sellValue: 10_000_000,
        constructionTime: 120, // 120 days
        upgradesFrom: 'mediumHeadquarters',
        productionLines: 3,
        productionCapacity: 150
      },
      smallFactory: {
        id: 'smallFactory',
        name: 'Small Factory',
        description: 'Basic manufacturing facility',
        cost: 3_000_000,
        capacity: {
          officeWorkers: 0,
          factoryWorkers: 40,
          technicalWorkers: 0,
        },
        maintenanceCost: 30_000,
        category: 'factory',
        unlockDate: { year: 2000, month: 1 },
        // reputationRequirement: 5, // COMMENTED OUT - may be added back later
        canSell: true,
        sellValue: 1_500_000,
        constructionTime: 45, // 45 days
        upgradesTo: 'largeFactory',
        upgradeCost: 6_500_000, // Cheaper than buying new
        upgradeTime: 60, // 60 days
        productionLines: 1, // Single production line
        productionCapacity: 100, // 100 units per day per line
      },
      largeFactory: {
        id: 'largeFactory',
        name: 'Large Factory',
        description: 'Advanced manufacturing complex with automation',
        cost: 10_000_000,
        capacity: {
          officeWorkers: 0,
          factoryWorkers: 150,
          technicalWorkers: 0,
        },
        maintenanceCost: 100_000,
        category: 'factory',
        unlockDate: { year: 2005, month: 6 },
        // reputationRequirement: 30, // COMMENTED OUT - may be added back later
        canSell: true,
        sellValue: 5_000_000,
        constructionTime: 90, // 90 days
        upgradesFrom: 'smallFactory',
        productionLines: 3, // Three production lines
        productionCapacity: 150, // 150 units per day per line
      },
      researchLab: {
        id: 'researchLab',
        name: 'Research Laboratory',
        description: 'Dedicated R&D facility',
        cost: 8_000_000,
        capacity: {
          officeWorkers: 0,
          factoryWorkers: 0,
          technicalWorkers: 60,
        },
        maintenanceCost: 60_000,
        category: 'lab',
        unlockDate: { year: 2008, month: 1 },
        // reputationRequirement: 20, // COMMENTED OUT - may be added back later
        canSell: true,
        sellValue: 4_000_000,
        constructionTime: 75, // 75 days
      }
    },
    
    // Employee groups
    employeeGroups: {
      factoryWorkers: {
        name: 'Assembly Workers',
        description: 'Manufacturing and quality control staff',
        roles: ['assemblyWorker', 'tester']
      },
      officeWorkers: {
        name: 'Office Workers',
        description: 'Business and creative staff',
        roles: ['designer', 'marketer', 'manager']
      },
      technicalWorkers: {
        name: 'Technical Workers',
        description: 'Engineering and research staff',
        roles: ['researcher', 'engineer']
      }
    },
    
    // Building categories
    categories: {
      headquarters: {
        name: 'Headquarters',
        description: 'Multi-purpose headquarters supporting all employee types',
        allowedGroups: ['officeWorkers', 'factoryWorkers', 'technicalWorkers']
      },
      office: {
        name: 'Office Buildings',
        description: 'Buildings for office workers (designers, marketers, managers)',
        allowedGroups: ['officeWorkers']
      },
      factory: {
        name: 'Manufacturing',
        description: 'Production facilities for factory workers',
        allowedGroups: ['factoryWorkers']
      },
      lab: {
        name: 'Laboratory',
        description: 'Research labs for technical workers',
        allowedGroups: ['technicalWorkers']
      }
    },
    
    // Capacity warnings
    capacityWarnings: {
      low: 0.8, // Warn when 80% full
      critical: 0.95 // Critical warning at 95% full
    }
  },

  // Inventory Management
  inventory: {
    // Component inventory
    components: {
      initialStockMultipliers: {
        budget: 5000,
        mid: 3000,
        premium: 2000,
        ultra: 1000
      },
      minStockLevel: 1000,
      maxStockLevel: 10000,
      leadTimeDays: 10, // 1 month delivery time
      orderSizes: [1000, 2000, 5000],
      orderShortcuts: [
        { label: '0', value: 0 },
        { label: '1k', value: 1000 },
        { label: '2k', value: 2000 },
        { label: '5k', value: 5000 },
        { label: '10k', value: 10000 },
      ],
      storageCostRate: 0.01, // 1% of inventory value per month
      reorderPoint: 0.3, // Reorder when stock falls below 30% of max
      safetyStockMultiplier: 1.5, // Safety stock = consumption rate * lead time * multiplier
      defaultAutoOrderQuantity: 1000, // Default quantity for auto-orders when not specified
    },
    
    // Device inventory
    devices: {
      initialStockRange: {
        production: { min: 5000, max: 10000 },
        discontinued: { min: 2500, max: 5000 }
      },
      targetStockLevel: 5000,
      baseProductionRate: 1000, // Units per month
      maxProductionRate: 10000,
      minProductionRate: 100,
      productionRateGrowth: 0.1 // 10% growth per month
    },
    
    // Alerts and warnings
    alerts: {
      stockoutRiskThresholds: {
        critical: 1, // Less than 1 month of stock
        high: 2,     // Less than 2 months of stock
        medium: 3    // Less than 3 months of stock
      },
      overstockThreshold: 2.0, // Alert when stock > 2x max level
      orderDelayProbability: 0.05, // 5% chance of order delay
      orderDelayDays: { min: 1, max: 7 }
    },
    
    // Financial
    orderingCosts: {
      baseOrderingCost: 1000, // Fixed cost per order
      bulkDiscounts: {
        1000: 0,    // No discount
        2000: 0.05, // 5% discount
        5000: 0.10  // 10% discount
      }
    }
  },

  // Newspaper (HQ Lobby)
  newspaper: {
    maxEvents: 5,
    animationDuration: 400, // ms
    storiesPerSpread: 2,
  },
} as const;

// Type-safe getter functions
export function getGameConstant<T extends keyof typeof GAME_CONSTANTS>(
  category: T
): typeof GAME_CONSTANTS[T] {
  return GAME_CONSTANTS[category];
}

// Helper functions for common calculations
export const GameCalculations = {
  getMarketValue: (marketShare: number) => 
    marketShare * GAME_CONSTANTS.finance.baseRevenuePerMarketShare,
  
  getOperationalCost: (revenue: number) => 
    revenue * GAME_CONSTANTS.finance.operationalCostsRatio,
  
  getTechScoreImpact: (techScore: number) => 
    techScore * GAME_CONSTANTS.productMetrics.techScoreWeight,
  
  getDefectRate: (techScore: number) => {
    const reduction = techScore * GAME_CONSTANTS.quality.techScoreDefectReduction;
    const baseRate = GAME_CONSTANTS.quality.baseDefectRate - reduction;
    return Math.max(
      GAME_CONSTANTS.quality.minDefectRate,
      Math.min(GAME_CONSTANTS.quality.maxDefectRate, baseRate)
    );
  }
};

// Export individual categories for convenience
export const TIME_CONSTANTS = GAME_CONSTANTS.time;
export const FINANCE_CONSTANTS = GAME_CONSTANTS.finance;
export const MARKET_CONSTANTS = GAME_CONSTANTS.market;
export const PRODUCTION_CONSTANTS = GAME_CONSTANTS.production;
export const DEVELOPMENT_CONSTANTS = GAME_CONSTANTS.development;
export const QUALITY_CONSTANTS = GAME_CONSTANTS.quality;
export const RESEARCH_CONSTANTS = GAME_CONSTANTS.research;
export const EMPLOYEE_CONSTANTS = GAME_CONSTANTS.employee;
export const DATABASE_CONSTANTS = GAME_CONSTANTS.database;
export const MARKET_SEGMENTS = GAME_CONSTANTS.marketSegments;
export const SALES_CONSTANTS = GAME_CONSTANTS.sales;
export const ECONOMIC_CONSTANTS = GAME_CONSTANTS.economic;
export const QUALITY_CONTROL_CONSTANTS = GAME_CONSTANTS.qualityControl;
export const BUILDINGS_CONSTANTS = GAME_CONSTANTS.buildings;
export const EMPLOYEE_ROLE_IDS = GAME_CONSTANTS.employee.roleIds;
export const INVENTORY_CONSTANTS = GAME_CONSTANTS.inventory;
export const DASHBOARD_CONSTANTS = GAME_CONSTANTS.dashboard;
export const NAVIGATION_CONSTANTS = GAME_CONSTANTS.navigation;
